/**
 * PersonSelector - Busca reutilizável de Pessoas por papel.
 *
 * RELEASE: 2026-10-19 v1.2.0 - Filtro rápido (quickFilter) aplica sozinho o estilo pill padrão
 *            (QUICK_FILTER_PILL) ao botão: sem borda cinza e cantos arredondados, igual aos
 *            demais filtros das barras de listagem. Campos de formulário seguem com borda.
 * RELEASE: 2026-10-06 v1.1.0 - Busca server-side com debounce e seleção estável.
 */
import { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronsUpDown, User } from 'lucide-react';
import { usePersonsList, type PersonRole } from '@/hooks/usePersons';
import { useStockManufacturers } from '@/hooks/useStockRegistries';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface PersonSelectorProps {
  value?: string | null;
  onSelect: (id: string | null) => void;
  role: PersonRole;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Nome conhecido mantém a seleção visível mesmo fora da página pesquisada. */
  selectedLabel?: string | null;
  /** Usa a consulta protegida pela permissão de Modelos do Estoque. */
  source?: 'persons' | 'stock-manufacturers';
  /** Texto da opção vazia (antes fixo em "Sem fabricante"). */
  noneLabel?: string;
  /** Filtro rápido mantém botão compacto, mas amplia a lista e permite nomes completos. */
  quickFilter?: boolean;
}

export function PersonSelector({
  value,
  onSelect,
  role,
  placeholder = 'Selecionar...',
  disabled = false,
  className,
  selectedLabel,
  source = 'persons',
  noneLabel,
  quickFilter = false,
}: PersonSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Evita uma consulta a cada tecla sem atrasar a digitação no campo.
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const personsQuery = usePersonsList({
    search: debouncedSearch,
    role,
    status: 'active',
    page: 1,
    pageSize: 50,
  }, source === 'persons');
  const manufacturersQuery = useStockManufacturers(debouncedSearch, source === 'stock-manufacturers');
  const items = source === 'stock-manufacturers'
    ? (manufacturersQuery.data ?? [])
    : (personsQuery.data?.items ?? []);
  const isLoading = source === 'stock-manufacturers' ? manufacturersQuery.isLoading : personsQuery.isLoading;

  const selectedPerson = items.find((person) => person.id === value);
  const visibleLabel = selectedPerson?.name ?? selectedLabel ?? placeholder;

  return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={disabled}
            // quickFilter = filtro rápido de listagem: recebe o pill padrão (sem borda) do botão.
            className={cn('w-full justify-between font-normal', quickFilter && QUICK_FILTER_PILL, className)}
          >
            <span className="truncate">
              {visibleLabel}
            </span>
            {quickFilter ? <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn('p-0', quickFilter ? 'w-[360px] max-w-[calc(100vw-2rem)]' : 'w-[var(--radix-popover-trigger-width)]')} align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? 'Buscando...' : 'Nenhum resultado.'}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onSelect(null);
                    setOpen(false);
                  }}
                >
                  <span className={cn('min-w-0 flex-1', quickFilter ? 'whitespace-normal break-words' : 'truncate')}>{noneLabel ?? placeholder}</span>
                  <Check className={cn('ml-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
                {items.map((person) => (
                  <CommandItem
                    key={person.id}
                    value={person.id}
                    onSelect={() => {
                      onSelect(person.id);
                      setOpen(false);
                    }}
                  >
                    <User className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <span className={cn('min-w-0 flex-1', quickFilter ? 'whitespace-normal break-words' : 'truncate')}>{person.name}</span>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4',
                        value === person.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
  );
}
